<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import { uploadImage } from "@/lib/uploads";
import { uploadCampaignPageFile, fetchCampaignPage, addCampaignPage, saveCampaignPage } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref(""), uploadingUrl = ref(false);
const form = ref({ imageUrl: "", title: "", content: "", url: "", sequence: "", isEnabled: false, isDynamic: false, showTitle: true, showDescription: true });
const imageUploader = (file, p) => uploadImage("campaign-page", file, p);

onMounted(async () => {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const p = await fetchCampaignPage(id.value);
    if (!p) { notFound.value = true; return; }
    form.value = { imageUrl: p.message?.imageUrl ?? "", title: p.message?.title ?? "", content: p.message?.desc ?? "", url: p.message?.url ?? "", sequence: p.sequence ?? "", isEnabled: p.isExternalBrowser ?? false, isDynamic: !!p.isDynamic, showTitle: p.showTitle ?? true, showDescription: p.showDescription ?? true };
  } catch { error.value = "Couldn't load the campaign page."; }
  finally { loading.value = false; }
});

async function onUrlFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  uploadingUrl.value = true;
  try { form.value.url = await uploadCampaignPageFile(file); }
  catch { error.value = "Couldn't upload the file."; }
  finally { uploadingUrl.value = false; }
}

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const f = form.value;
  if (!f.imageUrl) { validationError.value = "Cover photo is required."; return; }
  if (!f.title.trim()) { validationError.value = "Title is required."; return; }
  if (!f.url.trim()) { validationError.value = "URL is required."; return; }
  saving.value = true;
  try {
    if (isEdit.value) await saveCampaignPage({ title: f.title, desc: f.content, url: f.url, imageUrl: f.imageUrl, id: id.value, sequence: f.sequence, isExternalBrowser: f.isEnabled, isDynamic: f.isDynamic, showTitle: f.showTitle, showDescription: f.showDescription });
    else await addCampaignPage({ title: f.title, desc: f.content, url: f.url, imageUrl: f.imageUrl, sequence: f.sequence, isEnabled: f.isEnabled, isDynamic: f.isDynamic, showTitle: f.showTitle, showDescription: f.showDescription });
    success.value = true; setTimeout(() => router.push({ name: "campaign-pages" }), 800);
  } catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'campaign-pages' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Campaign Page" : "Add Campaign Page" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Campaign page not found.</VAlert>
    <VCard v-else class="pa-4" max-width="720">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <UploadField v-model="form.imageUrl" kind="image" label="Cover Photo" :uploader="imageUploader" class="mb-4" />
        <VRow>
          <VCol cols="9"><VTextField v-model="form.title" label="Title" /></VCol>
          <VCol cols="3" class="d-flex align-center"><VSwitch v-model="form.showTitle" label="Show Title" hide-details /></VCol>
          <VCol cols="9"><VTextarea v-model="form.content" label="Content" rows="3" /></VCol>
          <VCol cols="3" class="d-flex align-center"><VSwitch v-model="form.showDescription" label="Show Description" hide-details /></VCol>
          <VCol cols="12"><VTextField v-model="form.url" label="URL" placeholder="https://example.com/" /></VCol>
          <VCol cols="12">
            <div class="text-body-2 font-weight-semibold mb-1">Upload PDF / Image / Video (sets URL)</div>
            <input type="file" accept="image/*,application/pdf,video/*" @change="onUrlFile" />
            <span v-if="uploadingUrl" class="text-caption ml-2">Uploading…</span>
          </VCol>
          <VCol cols="6"><VTextField v-model="form.sequence" label="Sequence" type="number" /></VCol>
          <VCol cols="6" class="d-flex align-center gap-4">
            <VSwitch v-model="form.isDynamic" label="Dynamic Link" hide-details />
            <VSwitch v-model="form.isEnabled" label="External Browser" hide-details />
          </VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving" :disabled="uploadingUrl">Submit</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'campaign-pages' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
